/**
 * Modales para crear/editar Categorías y Actividades del evento.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalFooter,
  Button,
  Input,
  Select,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui";
import { toast } from "react-hot-toast";
import { Timestamp } from "firebase/firestore";

/**
 * Convierte un valor de tipo fecha (Timestamp | Date | string | number | undefined)
 * a milliseconds de forma segura. Devuelve 0 si no es convertible.
 * Útil para baselines/comparaciones cuando los datos pueden venir de Firestore
 * (Timestamp) o ya hidratados como Date.
 */
function scoredAtMillis(value: unknown): number {
  if (!value) return 0;
  const v = value as { toMillis?: () => number; toDate?: () => Date };
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.toDate === "function") return v.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const t = new Date(value).getTime();
    return isNaN(t) ? 0 : t;
  }
  return 0;
}
import {
  upsertCategory,
  upsertActivity,
  SCORE_MODE_LABEL,
  parseActivitiesCsv,
  importActivitiesFromCsv,
  CSV_TEMPLATE_EXAMPLE,
  CSV_TEMPLATE_HEADER,
} from "@/lib/services/eventActivities";
import {
  SUBMISSION_FIELD_TYPE_LABEL,
  SUBMISSION_TEMPLATES,
  slugifyFieldId,
  toJsDate,
  type SubmissionTemplateKey,
} from "@/lib/services/activitySubmissions";
import type { CsvParseResult } from "@/lib/services/eventActivities";
import type {
  ActivityScoreConfig,
  ActivityScoreMode,
  ActivitySubmissionField,
  ActivitySubmissionFieldType,
  ActivitySubmissionRequirements,
  EventActivity,
  EventActivityCategory,
  PodiumRubric,
} from "@/types";

const MODE_OPTIONS = (Object.keys(SCORE_MODE_LABEL) as ActivityScoreMode[]).map(
  (m) => ({ value: m, label: SCORE_MODE_LABEL[m] }),
);

/** Pequeño helper: Select que acepta `options` por conveniencia. */
function OptSelect({
  label,
  value,
  onChange,
  options,
}: {
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <Select label={label} value={value} onChange={onChange}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}

const COLOR_PRESETS = [
  "#7c3aed",
  "#0ea5e9",
  "#16a34a",
  "#f59e0b",
  "#9333ea",
  "#0891b2",
  "#10b981",
  "#ef4444",
  "#6366f1",
  "#db2777",
];

// ============================================================================
// Editor de configuración de puntaje (compartido)
// ============================================================================

function ScoreConfigEditor({
  mode,
  config,
  onChange,
}: {
  mode: ActivityScoreMode;
  config: ActivityScoreConfig;
  onChange: (c: ActivityScoreConfig) => void;
}) {
  const podium: PodiumRubric = config.podium ?? {
    first: 0,
    second: 0,
    third: 0,
    participation: 0,
  };

  if (mode === "podium") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="1° lugar"
          type="number"
          value={podium.first}
          onChange={(e) =>
            onChange({
              ...config,
              podium: { ...podium, first: Number(e.target.value) || 0 },
            })
          }
        />
        <Input
          label="2° lugar"
          type="number"
          value={podium.second}
          onChange={(e) =>
            onChange({
              ...config,
              podium: { ...podium, second: Number(e.target.value) || 0 },
            })
          }
        />
        <Input
          label="3° lugar"
          type="number"
          value={podium.third}
          onChange={(e) =>
            onChange({
              ...config,
              podium: { ...podium, third: Number(e.target.value) || 0 },
            })
          }
        />
        <Input
          label="Participación"
          type="number"
          value={podium.participation}
          onChange={(e) =>
            onChange({
              ...config,
              podium: {
                ...podium,
                participation: Number(e.target.value) || 0,
              },
            })
          }
        />
      </div>
    );
  }

  if (mode === "pass_fail") {
    return (
      <Input
        label="Puntos al cumplir"
        type="number"
        value={config.passFailValue ?? 0}
        onChange={(e) =>
          onChange({ ...config, passFailValue: Number(e.target.value) || 0 })
        }
      />
    );
  }

  if (mode === "fixed") {
    return (
      <Input
        label="Puntos fijos al entregar"
        type="number"
        value={config.fixedValue ?? 0}
        onChange={(e) =>
          onChange({ ...config, fixedValue: Number(e.target.value) || 0 })
        }
      />
    );
  }

  if (mode === "deductive") {
    return (
      <div>
        <Input
          label="Puntaje base"
          type="number"
          value={config.deductiveBase ?? 0}
          onChange={(e) =>
            onChange({ ...config, deductiveBase: Number(e.target.value) || 0 })
          }
        />
        <p className="mt-1 text-xs text-gray-500">
          Se restan puntos por faltas registradas.
        </p>
      </div>
    );
  }

  if (mode === "accumulative") {
    return (
      <div className="grid grid-cols-3 gap-3">
        <Input
          label="Base"
          type="number"
          value={config.accumulativeBase ?? 0}
          onChange={(e) =>
            onChange({
              ...config,
              accumulativeBase: Number(e.target.value) || 0,
            })
          }
        />
        <Input
          label="Por unidad"
          type="number"
          value={config.accumulativePerUnit ?? 0}
          onChange={(e) =>
            onChange({
              ...config,
              accumulativePerUnit: Number(e.target.value) || 0,
            })
          }
        />
        <Input
          label="Penalización"
          type="number"
          value={config.accumulativePenaltyPerUnit ?? 0}
          onChange={(e) =>
            onChange({
              ...config,
              accumulativePenaltyPerUnit: Number(e.target.value) || 0,
            })
          }
        />
      </div>
    );
  }

  if (mode === "timing") {
    const bands = config.timingBands ?? [];
    return (
      <div className="space-y-2">
        <p className="text-[11px] text-gray-500">
          Cada banda asigna puntos según la fecha de entrega. La{" "}
          <strong>primera banda cuya fecha tope sea ≥ hoy</strong> se aplica
          automáticamente. Deja la última banda <strong>sin fecha</strong> como
          fallback "tarde".
        </p>
        {bands.map((b, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4">
              <Input
                label={i === 0 ? "Etiqueta" : undefined}
                value={b.label}
                onChange={(e) => {
                  const copy = [...bands];
                  copy[i] = { ...copy[i], label: e.target.value };
                  onChange({ ...config, timingBands: copy });
                }}
              />
            </div>
            <div className="col-span-5">
              {i === 0 && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hasta (opcional)
                </label>
              )}
              <input
                type="datetime-local"
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                value={
                  b.untilDate
                    ? toLocalInput(toJsDate(b.untilDate) ?? new Date())
                    : ""
                }
                onChange={(e) => {
                  const copy = [...bands];
                  const v = e.target.value;
                  copy[i] = {
                    ...copy[i],
                    untilDate: v ? Timestamp.fromDate(new Date(v)) : undefined,
                  };
                  onChange({ ...config, timingBands: copy });
                }}
              />
            </div>
            <div className="col-span-2">
              <Input
                label={i === 0 ? "Puntos" : undefined}
                type="number"
                value={b.points}
                onChange={(e) => {
                  const copy = [...bands];
                  copy[i] = {
                    ...copy[i],
                    points: Number(e.target.value) || 0,
                  };
                  onChange({ ...config, timingBands: copy });
                }}
              />
            </div>
            <div className="col-span-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  onChange({
                    ...config,
                    timingBands: bands.filter((_, j) => j !== i),
                  })
                }
              >
                ✕
              </Button>
            </div>
          </div>
        ))}
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            onChange({
              ...config,
              timingBands: [...bands, { label: "Nueva banda", points: 0 }],
            })
          }
        >
          + Agregar banda
        </Button>
      </div>
    );
  }

  return null;
}

// ============================================================================
// Modal: Categoría
// ============================================================================

export function CategoryFormModal({
  isOpen,
  onClose,
  eventId,
  category,
  nextOrder,
}: {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  category?: EventActivityCategory | null;
  nextOrder: number;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<string>(COLOR_PRESETS[0]);
  const [order, setOrder] = useState<number>(nextOrder);
  const [mode, setMode] = useState<ActivityScoreMode>("podium");
  const [config, setConfig] = useState<ActivityScoreConfig>({
    podium: { first: 10000, second: 7000, third: 5000, participation: 2000 },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setName(category.name);
      setDescription(category.description ?? "");
      setColor(category.color ?? COLOR_PRESETS[0]);
      setOrder(category.order);
      setMode(category.defaultScoreMode);
      setConfig(category.defaultScoreConfig ?? {});
    } else {
      setName("");
      setDescription("");
      setColor(COLOR_PRESETS[0]);
      setOrder(nextOrder);
      setMode("podium");
      setConfig({
        podium: {
          first: 10000,
          second: 7000,
          third: 5000,
          participation: 2000,
        },
      });
    }
  }, [category, nextOrder, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Ingresa un nombre");
      return;
    }
    setSaving(true);
    try {
      await upsertCategory({
        eventId,
        name,
        description: description || undefined,
        color,
        order,
        defaultScoreMode: mode,
        defaultScoreConfig: config,
        slug: category?.slug,
      });
      toast.success(category ? "Categoría actualizada" : "Categoría creada");
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={category ? "Editar categoría" : "Nueva categoría"}
      size="lg"
    >
      <div className="space-y-4">
        <Input
          label="Nombre"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej: Espirituales"
        />
        <Input
          label="Descripción (opcional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Color
          </label>
          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                className={`w-8 h-8 rounded-full border-2 ${
                  color === c ? "border-gray-900" : "border-transparent"
                }`}
                style={{ backgroundColor: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </div>
        <Input
          label="Orden"
          type="number"
          value={order}
          onChange={(e) => setOrder(Number(e.target.value) || 0)}
        />
        <OptSelect
          label="Modo de puntaje por defecto"
          value={mode}
          onChange={(e) => setMode(e.target.value as ActivityScoreMode)}
          options={MODE_OPTIONS}
        />
        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
          <p className="text-xs font-medium text-gray-700 mb-2">
            Configuración del modo
          </p>
          <ScoreConfigEditor mode={mode} config={config} onChange={setConfig} />
        </div>
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Guardando..." : category ? "Guardar cambios" : "Crear"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ============================================================================
// Modal: Actividad
// ============================================================================

export function ActivityFormModal({
  isOpen,
  onClose,
  eventId,
  category,
  activity,
  nextOrder,
}: {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  category: EventActivityCategory;
  activity?: EventActivity | null;
  nextOrder: number;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [order, setOrder] = useState<number>(nextOrder);
  const [mode, setMode] = useState<ActivityScoreMode>(
    category.defaultScoreMode,
  );
  const [config, setConfig] = useState<ActivityScoreConfig>(
    category.defaultScoreConfig,
  );
  const [participantsMode, setParticipantsMode] = useState<
    "team" | "individual"
  >("team");
  const [participantsMin, setParticipantsMin] = useState<number>(1);
  const [participantsMax, setParticipantsMax] = useState<number>(10);
  const [saving, setSaving] = useState(false);

  // === Entregable previo (formulario dinámico) ===
  const [submEnabled, setSubmEnabled] = useState(false);
  const [submOpensAt, setSubmOpensAt] = useState<string>(""); // datetime-local
  const [submDeadline, setSubmDeadline] = useState<string>(""); // datetime-local
  const [submAllowLate, setSubmAllowLate] = useState(false);
  const [submNotifyOnOpen, setSubmNotifyOnOpen] = useState(false);
  const [submInstructions, setSubmInstructions] = useState("");
  const [submFields, setSubmFields] = useState<ActivitySubmissionField[]>([]);

  useEffect(() => {
    if (activity) {
      setName(activity.name);
      setDescription(activity.description ?? "");
      setLongDescription(activity.longDescription ?? "");
      setOrder(activity.order);
      setMode(activity.scoreMode);
      setConfig(activity.scoreConfig);
      setParticipantsMode(activity.participants.mode);
      setParticipantsMin(activity.participants.min ?? 1);
      setParticipantsMax(activity.participants.max ?? 10);
      const req = activity.submissionRequirements;
      setSubmEnabled(req?.enabled ?? false);
      setSubmOpensAt(
        req?.opensAt ? toLocalInput(toJsDate(req.opensAt) ?? new Date()) : "",
      );
      setSubmDeadline(
        req?.deadline ? toLocalInput(toJsDate(req.deadline) ?? new Date()) : "",
      );
      setSubmAllowLate(req?.allowLate ?? false);
      setSubmNotifyOnOpen(req?.notifyOnOpen ?? false);
      setSubmInstructions(req?.instructions ?? "");
      setSubmFields(
        req?.fields ? [...req.fields].sort((a, b) => a.order - b.order) : [],
      );
    } else {
      setName("");
      setDescription("");
      setLongDescription("");
      setOrder(nextOrder);
      setMode(category.defaultScoreMode);
      setConfig(category.defaultScoreConfig);
      setParticipantsMode("team");
      setParticipantsMin(1);
      setParticipantsMax(10);
      setSubmEnabled(false);
      setSubmOpensAt("");
      setSubmDeadline("");
      setSubmAllowLate(false);
      setSubmNotifyOnOpen(false);
      setSubmInstructions("");
      setSubmFields([]);
    }
  }, [activity, category, nextOrder, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Ingresa un nombre");
      return;
    }
    // Validar campos del entregable
    if (submEnabled) {
      if (submFields.length === 0) {
        toast.error("Agrega al menos un campo al entregable");
        return;
      }
      const ids = new Set<string>();
      for (const f of submFields) {
        if (!f.label.trim()) {
          toast.error("Todos los campos deben tener etiqueta");
          return;
        }
        if (ids.has(f.id)) {
          toast.error(`ID de campo duplicado: ${f.id}`);
          return;
        }
        ids.add(f.id);
      }
    }

    let submissionRequirements: ActivitySubmissionRequirements | undefined;
    if (submEnabled) {
      submissionRequirements = {
        enabled: true,
        allowLate: submAllowLate,
        notifyOnOpen: submNotifyOnOpen,
        fields: submFields.map((f, idx) => ({ ...f, order: (idx + 1) * 10 })),
      };
      if (submDeadline) {
        submissionRequirements.deadline = Timestamp.fromDate(
          new Date(submDeadline),
        );
      }
      if (submOpensAt) {
        submissionRequirements.opensAt = Timestamp.fromDate(
          new Date(submOpensAt),
        );
      }
      if (
        submissionRequirements.opensAt &&
        submissionRequirements.deadline &&
        submissionRequirements.opensAt.toMillis() >
          submissionRequirements.deadline.toMillis()
      ) {
        toast.error("La fecha de apertura no puede ser posterior al deadline");
        setSaving(false);
        return;
      }
      if (submInstructions.trim()) {
        submissionRequirements.instructions = submInstructions.trim();
      }
      // Conserva la marca de notificación si la fecha de apertura no cambió.
      const prevReq = activity?.submissionRequirements;
      const prevOpensMs = prevReq?.opensAt?.toMillis?.();
      const newOpensMs = submissionRequirements.opensAt?.toMillis?.();
      if (
        prevReq?.openNotifiedAt &&
        prevOpensMs &&
        newOpensMs &&
        prevOpensMs === newOpensMs
      ) {
        submissionRequirements.openNotifiedAt = prevReq.openNotifiedAt;
      }
    } else if (activity?.submissionRequirements) {
      // Apagar pero conservar campos para no perder configuración
      submissionRequirements = {
        ...activity.submissionRequirements,
        enabled: false,
      };
    }

    setSaving(true);
    try {
      await upsertActivity({
        eventId,
        categoryId: category.id,
        name,
        description: description || undefined,
        longDescription: longDescription || undefined,
        scoreMode: mode,
        scoreConfig: config,
        participants:
          participantsMode === "team"
            ? { mode: "team" }
            : {
                mode: "individual",
                min: participantsMin,
                max: participantsMax,
              },
        order,
        slug: activity?.slug,
        submissionRequirements,
      });
      toast.success(activity ? "Actividad actualizada" : "Actividad creada");
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={activity ? "Editar actividad" : "Nueva actividad"}
      description={`Categoría: ${category.name}`}
      size="lg"
    >
      <Tabs defaultValue="basic">
        <TabsList variant="underline" className="mb-4">
          <TabsTrigger value="basic" variant="underline">
            Datos
          </TabsTrigger>
          <TabsTrigger value="score" variant="underline">
            Puntaje
          </TabsTrigger>
          <TabsTrigger value="submission" variant="underline">
            Entregable{" "}
            {submEnabled && (
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-blue-500" />
            )}
          </TabsTrigger>
        </TabsList>

        {/* === Tab: Datos === */}
        <TabsContent value="basic">
          <div className="space-y-4">
            <Input
              label="Nombre"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Marcha y orden cerrado"
            />
            <Input
              label="Descripción corta"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Resumen breve para el listado"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instrucciones largas (opcional)
              </label>
              <textarea
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                rows={4}
                value={longDescription}
                onChange={(e) => setLongDescription(e.target.value)}
                placeholder="Reglas, requisitos, materiales, etc."
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Texto extendido que verá el director al abrir la actividad
                (reglas detalladas, formato, materiales, criterios de
                evaluación). Si no aplica, déjalo vacío.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Input
                  label="Orden"
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(Number(e.target.value) || 0)}
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Posición en la lista dentro de la categoría. Menor número =
                  aparece primero (10, 20, 30…). No afecta puntos.
                </p>
              </div>
              <div>
                <OptSelect
                  label="Participantes"
                  value={participantsMode}
                  onChange={(e) =>
                    setParticipantsMode(e.target.value as "team" | "individual")
                  }
                  options={[
                    { value: "team", label: "Todo el club" },
                    { value: "individual", label: "N participantes" },
                  ]}
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  <strong>Todo el club</strong>: el club entrega/participa como
                  unidad (ej. Informe Mensual, marchas).{" "}
                  <strong>N participantes</strong>: requiere indicar cuántos
                  miembros participan (ej. fútbol = 5–7).
                </p>
              </div>
            </div>
            {participantsMode === "individual" && (
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Mínimo"
                  type="number"
                  value={participantsMin}
                  onChange={(e) =>
                    setParticipantsMin(Number(e.target.value) || 1)
                  }
                />
                <Input
                  label="Máximo"
                  type="number"
                  value={participantsMax}
                  onChange={(e) =>
                    setParticipantsMax(Number(e.target.value) || 1)
                  }
                />
              </div>
            )}
          </div>
        </TabsContent>

        {/* === Tab: Puntaje === */}
        <TabsContent value="score">
          <div className="space-y-4">
            <OptSelect
              label="Modo de puntaje"
              value={mode}
              onChange={(e) => setMode(e.target.value as ActivityScoreMode)}
              options={MODE_OPTIONS}
            />
            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
              <p className="text-xs font-medium text-gray-700 mb-2">
                Configuración del modo
              </p>
              <ScoreConfigEditor
                mode={mode}
                config={config}
                onChange={setConfig}
              />
            </div>
            <p className="text-xs text-gray-500">
              Tip: el modo por defecto de la categoría es{" "}
              <span className="font-medium">
                {SCORE_MODE_LABEL[category.defaultScoreMode]}
              </span>
              . Puedes sobrescribirlo para esta actividad.
            </p>
          </div>
        </TabsContent>

        {/* === Tab: Entregable === */}
        <TabsContent value="submission">
          <div className="space-y-4">
            <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg bg-blue-50 border border-blue-200">
              <input
                type="checkbox"
                checked={submEnabled}
                onChange={(e) => setSubmEnabled(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium text-blue-900">
                Esta actividad requiere un entregable previo
              </span>
            </label>

            {!submEnabled && (
              <p className="text-xs text-gray-500 italic">
                Activa la casilla para definir un formulario que los directores
                deben completar antes del evento (archivos, texto, etc.).
              </p>
            )}

            {submEnabled && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de apertura
                    </label>
                    <input
                      type="datetime-local"
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                      value={submOpensAt}
                      onChange={(e) => setSubmOpensAt(e.target.value)}
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Antes de esta fecha el director no podrá iniciar la
                      entrega.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha límite
                    </label>
                    <input
                      type="datetime-local"
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                      value={submDeadline}
                      onChange={(e) => setSubmDeadline(e.target.value)}
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer md:col-span-2">
                    <input
                      type="checkbox"
                      checked={submAllowLate}
                      onChange={(e) => setSubmAllowLate(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Permitir entregas después del límite
                    </span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer md:col-span-2">
                    <input
                      type="checkbox"
                      checked={submNotifyOnOpen}
                      onChange={(e) => setSubmNotifyOnOpen(e.target.checked)}
                      className="rounded mt-0.5"
                    />
                    <span className="text-sm text-gray-700">
                      Notificar a los directores por correo cuando se abra la
                      entrega
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Se enviará un email + notificación in-app al director
                        principal de cada club inscrito en el evento al alcanzar
                        la fecha de apertura.
                      </span>
                    </span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Instrucciones para los directores (opcional)
                  </label>
                  <textarea
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                    rows={2}
                    value={submInstructions}
                    onChange={(e) => setSubmInstructions(e.target.value)}
                  />
                </div>

                <div className="rounded-lg border border-gray-200 p-3 bg-white">
                  <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-700">
                      Campos del formulario
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        ({submFields.length})
                      </span>
                    </p>
                    <div className="flex gap-2">
                      <select
                        className="text-xs p-1.5 border border-gray-300 rounded"
                        defaultValue=""
                        onChange={(e) => {
                          const key = e.target.value as
                            | SubmissionTemplateKey
                            | "";
                          if (!key) return;
                          const tpl = SUBMISSION_TEMPLATES[key];
                          setSubmFields([...tpl.fields]);
                          e.target.value = "";
                          toast.success(`Plantilla cargada: ${tpl.label}`);
                        }}
                      >
                        <option value="">Cargar plantilla…</option>
                        {Object.entries(SUBMISSION_TEMPLATES).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v.label}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() =>
                          setSubmFields((prev) => [
                            ...prev,
                            {
                              id: `campo_${prev.length + 1}`,
                              label: "",
                              type: "text_short",
                              required: false,
                              order: (prev.length + 1) * 10,
                            },
                          ])
                        }
                      >
                        + Campo
                      </Button>
                    </div>
                  </div>
                  {submFields.length === 0 ? (
                    <div className="text-center py-6 text-sm text-gray-500">
                      <p className="mb-1">Sin campos todavía.</p>
                      <p className="text-xs">
                        Agrega un campo o carga una plantilla.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {submFields.map((f, idx) => (
                        <SubmissionFieldRow
                          key={idx}
                          field={f}
                          index={idx}
                          total={submFields.length}
                          onChange={(updated) =>
                            setSubmFields((prev) =>
                              prev.map((x, i) => (i === idx ? updated : x)),
                            )
                          }
                          onDelete={() =>
                            setSubmFields((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          onMove={(delta) =>
                            setSubmFields((prev) => {
                              const arr = [...prev];
                              const tgt = idx + delta;
                              if (tgt < 0 || tgt >= arr.length) return prev;
                              [arr[idx], arr[tgt]] = [arr[tgt], arr[idx]];
                              return arr;
                            })
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Guardando..." : activity ? "Guardar cambios" : "Crear"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ============================================================================
// Modal: Capturar puntaje de un club
// ============================================================================

import {
  upsertScore,
  computePoints,
  deleteScore,
  buildScoreId,
} from "@/lib/services/eventActivities";
import { useAuthStore } from "@/stores/authStore";
import type {
  ActivityScoreInput,
  Club,
  ClubActivityScore,
  ClubActivitySubmission,
} from "@/types";

export function ActivityScoreModal({
  isOpen,
  onClose,
  eventId,
  activity,
  category,
  clubs,
  existingScores,
}: {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  activity: EventActivity;
  category: EventActivityCategory;
  clubs: Array<{ id: string; name: string }>;
  existingScores: ClubActivityScore[];
}) {
  const { user } = useAuthStore();
  const [clubId, setClubId] = useState<string>(clubs[0]?.id ?? "");
  const [input, setInput] = useState<ActivityScoreInput>({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const existing = useMemo(
    () => existingScores.find((s) => s.clubId === clubId),
    [existingScores, clubId],
  );

  useEffect(() => {
    if (existing) {
      setInput(existing.input);
      setNotes(existing.notes ?? "");
    } else {
      setInput({});
      setNotes("");
    }
  }, [existing, isOpen]);

  const previewPoints = useMemo(
    () => computePoints(activity.scoreMode, activity.scoreConfig, input),
    [activity, input],
  );

  const handleSave = async () => {
    if (!clubId) {
      toast.error("Selecciona un club");
      return;
    }
    setSaving(true);
    try {
      const club = clubs.find((c) => c.id === clubId);
      await upsertScore({
        eventId,
        activityId: activity.id,
        activityName: activity.name,
        categoryId: category.id,
        categoryName: category.name,
        clubId,
        clubName: club?.name ?? clubId,
        scoreMode: activity.scoreMode,
        scoreConfig: activity.scoreConfig,
        input,
        notes: notes || undefined,
        scoredByName: user?.displayName || user?.email || "Usuario",
      });
      toast.success(`Puntaje guardado: ${previewPoints} pts`);
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Registrar puntaje · ${activity.name}`}
      description={category.name}
      size="lg"
    >
      <div className="space-y-4">
        <OptSelect
          label="Club"
          value={clubId}
          onChange={(e) => setClubId(e.target.value)}
          options={clubs.map((c) => ({ value: c.id, label: c.name }))}
        />

        <ScoreInputEditor
          mode={activity.scoreMode}
          config={activity.scoreConfig}
          value={input}
          onChange={setInput}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notas (opcional)
          </label>
          <textarea
            className="w-full p-2 border border-gray-300 rounded-lg text-sm"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 flex items-center justify-between">
          <span className="text-sm font-medium text-purple-900">
            Puntos calculados
          </span>
          <span className="text-2xl font-bold text-purple-700">
            {previewPoints.toLocaleString()}
          </span>
        </div>

        {existing && (
          <p className="text-xs text-amber-700">
            Ya existe un puntaje para este club. Se sobrescribirá.
          </p>
        )}
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Guardando..." : "Guardar puntaje"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// Editor de input según modo
function ScoreInputEditor({
  mode,
  config,
  value,
  onChange,
}: {
  mode: ActivityScoreMode;
  config: ActivityScoreConfig;
  value: ActivityScoreInput;
  onChange: (v: ActivityScoreInput) => void;
}) {
  if (mode === "podium") {
    return (
      <OptSelect
        label="Posición"
        value={value.position ?? ""}
        onChange={(e) =>
          onChange({
            ...value,
            position: (e.target.value || undefined) as
              | "first"
              | "second"
              | "third"
              | "participation"
              | undefined,
          })
        }
        options={[
          { value: "", label: "— Selecciona —" },
          { value: "first", label: "1° lugar" },
          { value: "second", label: "2° lugar" },
          { value: "third", label: "3° lugar" },
          { value: "participation", label: "Participación" },
        ]}
      />
    );
  }
  if (mode === "pass_fail") {
    return (
      <OptSelect
        label="Resultado"
        value={value.passed === undefined ? "" : value.passed ? "yes" : "no"}
        onChange={(e) =>
          onChange({
            ...value,
            passed:
              e.target.value === "" ? undefined : e.target.value === "yes",
          })
        }
        options={[
          { value: "", label: "— Selecciona —" },
          { value: "yes", label: "Cumple" },
          { value: "no", label: "No cumple" },
        ]}
      />
    );
  }
  if (mode === "fixed") {
    return (
      <OptSelect
        label="Entrega"
        value={
          value.delivered === undefined ? "" : value.delivered ? "yes" : "no"
        }
        onChange={(e) =>
          onChange({
            ...value,
            delivered:
              e.target.value === "" ? undefined : e.target.value === "yes",
          })
        }
        options={[
          { value: "", label: "— Selecciona —" },
          { value: "yes", label: "Entregado" },
          { value: "no", label: "No entregado" },
        ]}
      />
    );
  }
  if (mode === "deductive") {
    return (
      <div>
        <Input
          label="Puntos a descontar"
          type="number"
          value={value.deductionPoints ?? 0}
          onChange={(e) =>
            onChange({
              ...value,
              deductionPoints: Number(e.target.value) || 0,
            })
          }
        />
        <p className="mt-1 text-xs text-gray-500">
          Base: {config.deductiveBase ?? 0} pts
        </p>
      </div>
    );
  }
  if (mode === "accumulative") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Unidades cumplidas"
          type="number"
          value={value.unitsCompleted ?? 0}
          onChange={(e) =>
            onChange({
              ...value,
              unitsCompleted: Number(e.target.value) || 0,
            })
          }
        />
        <Input
          label="Penalizaciones"
          type="number"
          value={value.penaltyUnits ?? 0}
          onChange={(e) =>
            onChange({
              ...value,
              penaltyUnits: Number(e.target.value) || 0,
            })
          }
        />
      </div>
    );
  }
  if (mode === "timing") {
    const bands = config.timingBands ?? [];
    return (
      <OptSelect
        label="Banda de entrega"
        value={value.timingBandIndex?.toString() ?? ""}
        onChange={(e) =>
          onChange({
            ...value,
            timingBandIndex:
              e.target.value === "" ? undefined : Number(e.target.value),
          })
        }
        options={[
          { value: "", label: "— Selecciona —" },
          ...bands.map((b, i) => ({
            value: i.toString(),
            label: `${b.label} (${b.points} pts)`,
          })),
        ]}
      />
    );
  }
  return null;
}

// Re-export para conveniencia
export type { Club };

// ============================================================================
// CsvImportModal — Importación masiva pegando un CSV
// ============================================================================

export function CsvImportModal({
  open,
  onClose,
  eventId,
  existingCategories,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string;
  existingCategories: EventActivityCategory[];
  onImported: () => void;
}) {
  const [csv, setCsv] = useState("");
  const [saving, setSaving] = useState(false);

  const parsed:
    | { ok: true; data: CsvParseResult }
    | { ok: false; error: string }
    | null = useMemo(() => {
    const trimmed = csv.trim();
    if (!trimmed) return null;
    try {
      return { ok: true, data: parseActivitiesCsv(trimmed) };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }, [csv]);

  useEffect(() => {
    if (!open) {
      setCsv("");
      setSaving(false);
    }
  }, [open]);

  const handleLoadExample = () => setCsv(CSV_TEMPLATE_EXAMPLE);

  const handleImport = async () => {
    if (!parsed || !parsed.ok) return;
    setSaving(true);
    try {
      const summary = await importActivitiesFromCsv(
        eventId,
        parsed.data,
        existingCategories,
      );
      toast.success(
        `Importado: ${summary.categoriesCreated} categorías, ${summary.activitiesCreated} actividades.`,
      );
      if (summary.skipped.length > 0) {
        toast(
          `${summary.skipped.length} elemento(s) omitido(s). Revisa la consola.`,
          { icon: "⚠️" },
        );
        // eslint-disable-next-line no-console
        console.warn("CSV import skipped:", summary.skipped);
      }
      onImported();
      onClose();
    } catch (e) {
      toast.error(`Error al importar: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Importar actividades desde CSV"
      size="xl"
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
          <p className="font-semibold mb-1">Formato esperado</p>
          <p className="font-mono mb-2">{CSV_TEMPLATE_HEADER}</p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <code>tipo</code>: <b>C</b> (categoría) o <b>A</b> (actividad).
            </li>
            <li>
              <code>modo</code>: <code>podium</code>, <code>pass_fail</code>,{" "}
              <code>fixed</code>, <code>deductive</code>,{" "}
              <code>accumulative</code>, <code>timing</code>. En actividades
              vacío = hereda de la categoría.
            </li>
            <li>
              <code>puntos</code> separados por <code>;</code>:
              <ul className="list-disc list-inside ml-4">
                <li>
                  podium → <code>10000;7000;5000;2000</code>
                </li>
                <li>pass_fail / fixed / deductive → un número</li>
                <li>
                  accumulative → <code>base;perUnit;penalty</code>
                </li>
                <li>
                  timing → <code>Etiqueta:pts;Etiqueta:pts</code>
                </li>
              </ul>
            </li>
            <li>
              Si un campo contiene comas, enciérralo en comillas dobles{" "}
              <code>"…"</code>.
            </li>
            <li className="pt-1 border-t border-blue-200">
              <b>Entregables (F4)</b> — solo en filas <code>A</code>:
              <ul className="list-disc list-inside ml-4">
                <li>
                  <code>entregable</code>: <code>1</code> activa el formulario
                  de entrega para directores.
                </li>
                <li>
                  <code>plantilla</code>: <code>informe_mensual</code>,{" "}
                  <code>video_previos</code>, <code>carpeta_club</code>,{" "}
                  <code>single_file</code>, <code>single_url</code>. Vacío =
                  <code> single_file</code>.
                </li>
                <li>
                  <code>apertura</code>: <code>YYYY-MM-DD HH:mm</code>{" "}
                  (opcional). Antes de esta fecha el director no podrá iniciar
                  la entrega.
                </li>
                <li>
                  <code>deadline</code>: <code>YYYY-MM-DD HH:mm</code>.
                </li>
                <li>
                  <code>permite_tarde</code>: <code>1</code> permite entrega
                  tardía (queda marcada).
                </li>
                <li>
                  <code>notificar</code>: <code>1</code> envía email +
                  notificación in-app a los directores cuando llegue la fecha de
                  apertura.
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">
            CSV a importar
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleLoadExample}
          >
            Cargar ejemplo
          </Button>
        </div>

        <textarea
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={12}
          className="w-full font-mono text-xs rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
          placeholder={
            CSV_TEMPLATE_HEADER +
            "\nC,Espirituales,,podium,10000;7000;5000;2000,...\nA,Espirituales,Matutina,,,...\n..."
          }
        />

        {parsed && !parsed.ok && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {parsed.error}
          </div>
        )}

        {parsed && parsed.ok && (
          <div className="rounded-md bg-purple-50 border border-purple-200 p-3 text-sm">
            <div className="font-semibold text-purple-900 mb-2">
              Vista previa
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="font-medium text-gray-700 mb-1">
                  Categorías ({parsed.data.categories.length})
                </div>
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {parsed.data.categories.map((c) => (
                    <li key={c.name} className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ background: c.color || "#999" }}
                      />
                      <span className="font-medium">{c.name}</span>
                      <span className="text-gray-500">
                        — {SCORE_MODE_LABEL[c.scoreMode]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="font-medium text-gray-700 mb-1">
                  Actividades ({parsed.data.activities.length})
                </div>
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                  {parsed.data.activities.map((a, i) => (
                    <li key={`${a.categoryName}-${a.name}-${i}`}>
                      <span className="text-gray-500">{a.categoryName}</span> →{" "}
                      <span className="font-medium">{a.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {parsed.data.warnings.length > 0 && (
              <div className="mt-2 text-xs text-amber-700">
                <div className="font-medium">Avisos:</div>
                <ul className="list-disc list-inside">
                  {parsed.data.warnings.slice(0, 5).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleImport}
          disabled={!parsed || !parsed.ok || saving}
          loading={saving}
        >
          Importar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ============================================================================
// SubmissionFieldRow — Editor inline de un campo del formulario
// ============================================================================

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function SubmissionFieldRow({
  field,
  index,
  total,
  onChange,
  onDelete,
  onMove,
}: {
  field: ActivitySubmissionField;
  index: number;
  total: number;
  onChange: (f: ActivitySubmissionField) => void;
  onDelete: () => void;
  onMove: (delta: number) => void;
}) {
  const update = (patch: Partial<ActivitySubmissionField>) =>
    onChange({ ...field, ...patch });

  const needsOptions = field.type === "select" || field.type === "multi_select";

  return (
    <div className="p-2 bg-white rounded border border-gray-200 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400 w-6">#{index + 1}</span>
        <input
          type="text"
          placeholder="Etiqueta"
          className="flex-1 p-1.5 text-sm border border-gray-300 rounded"
          value={field.label}
          onChange={(e) => {
            const label = e.target.value;
            // Auto-generar id si está vacío o si coincide con el label previo slugificado
            const newId =
              !field.id || field.id.startsWith("campo_")
                ? slugifyFieldId(label) || field.id
                : field.id;
            update({ label, id: newId });
          }}
        />
        <select
          className="text-xs p-1 border border-gray-300 rounded"
          value={field.type}
          onChange={(e) =>
            update({ type: e.target.value as ActivitySubmissionFieldType })
          }
        >
          {(
            Object.keys(
              SUBMISSION_FIELD_TYPE_LABEL,
            ) as ActivitySubmissionFieldType[]
          ).map((t) => (
            <option key={t} value={t}>
              {SUBMISSION_FIELD_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={field.required}
            onChange={(e) => update({ required: e.target.checked })}
          />
          Req.
        </label>
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          className="text-xs px-1 disabled:opacity-30"
          title="Subir"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={index === total - 1}
          className="text-xs px-1 disabled:opacity-30"
          title="Bajar"
        >
          ↓
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs text-red-600 px-1"
          title="Eliminar"
        >
          ✕
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 pl-8">
        <input
          type="text"
          placeholder="ID interno (slug)"
          className="p-1 text-xs border border-gray-200 rounded font-mono"
          value={field.id}
          onChange={(e) => update({ id: slugifyFieldId(e.target.value) })}
        />
        <input
          type="text"
          placeholder="Texto de ayuda (opcional)"
          className="p-1 text-xs border border-gray-200 rounded"
          value={field.helpText ?? ""}
          onChange={(e) => update({ helpText: e.target.value || undefined })}
        />
      </div>
      {needsOptions && (
        <div className="pl-8">
          <input
            type="text"
            placeholder="Opciones separadas por coma (ej: Sí, No, Tal vez)"
            className="w-full p-1 text-xs border border-gray-200 rounded"
            value={(field.options ?? []).join(", ")}
            onChange={(e) =>
              update({
                options: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Modal: Captura batch de puntajes (UX invertida — posición → clubes)
// ============================================================================

/**
 * Para los modos "categóricos" (podium, pass_fail, fixed, timing) este modal
 * muestra una tabla: una fila por club inscrito, una columna por opción del
 * modo (1°/2°/3°/Participación, Cumple/No cumple, Entregado/No, o cada
 * banda de tiempo). El admin marca con un radio. Al guardar se hace upsert
 * solo de los clubes con cambios respecto a `existingScores`. Modos numéricos
 * (deductive/accumulative) siguen usando `ActivityScoreModal`.
 */

type BatchOption = {
  key: string;
  label: string;
  points: number;
  /** True si este input corresponde a esta opción. */
  matches: (input: ActivityScoreInput) => boolean;
  /** Aplica la opción al input (sobrescribiendo la dimensión relevante). */
  apply: (input: ActivityScoreInput) => ActivityScoreInput;
  /** Si la opción se considera "exclusiva globalmente" (ej: solo un 1° lugar). */
  exclusive?: boolean;
};

function buildBatchOptions(
  mode: ActivityScoreMode,
  config: ActivityScoreConfig,
): BatchOption[] | null {
  if (mode === "podium") {
    const r: PodiumRubric = config.podium ?? {
      first: 0,
      second: 0,
      third: 0,
      participation: 0,
    };
    return [
      {
        key: "first",
        label: "1°",
        points: r.first,
        matches: (i) => i.position === "first",
        apply: (i) => ({ ...i, position: "first" }),
      },
      {
        key: "second",
        label: "2°",
        points: r.second,
        matches: (i) => i.position === "second",
        apply: (i) => ({ ...i, position: "second" }),
      },
      {
        key: "third",
        label: "3°",
        points: r.third,
        matches: (i) => i.position === "third",
        apply: (i) => ({ ...i, position: "third" }),
      },
      {
        key: "participation",
        label: "Participación",
        points: r.participation,
        matches: (i) => i.position === "participation",
        apply: (i) => ({ ...i, position: "participation" }),
      },
    ];
  }
  if (mode === "pass_fail") {
    const pts = config.passFailValue ?? 0;
    return [
      {
        key: "yes",
        label: "Cumple",
        points: pts,
        matches: (i) => i.passed === true,
        apply: (i) => ({ ...i, passed: true }),
      },
      {
        key: "no",
        label: "No cumple",
        points: 0,
        matches: (i) => i.passed === false,
        apply: (i) => ({ ...i, passed: false }),
      },
    ];
  }
  if (mode === "fixed") {
    const pts = config.fixedValue ?? 0;
    return [
      {
        key: "yes",
        label: "Entregado",
        points: pts,
        matches: (i) => i.delivered === true,
        apply: (i) => ({ ...i, delivered: true }),
      },
      {
        key: "no",
        label: "No entregado",
        points: 0,
        matches: (i) => i.delivered === false,
        apply: (i) => ({ ...i, delivered: false }),
      },
    ];
  }
  if (mode === "timing") {
    const bands = config.timingBands ?? [];
    return bands.map((b, idx) => ({
      key: `b${idx}`,
      label: b.label,
      points: b.points,
      matches: (i) => i.timingBandIndex === idx,
      apply: (i) => ({ ...i, timingBandIndex: idx }),
    }));
  }
  return null; // deductive / accumulative no soportados aquí
}

/** Comparación superficial relevante para los modos categóricos. */
function sameInput(a: ActivityScoreInput, b: ActivityScoreInput): boolean {
  return (
    a.position === b.position &&
    a.passed === b.passed &&
    a.delivered === b.delivered &&
    a.timingBandIndex === b.timingBandIndex
  );
}

export function ActivityBatchScoreModal({
  isOpen,
  onClose,
  eventId,
  activity,
  category,
  clubs,
  existingScores,
  submissions,
}: {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  activity: EventActivity;
  category: EventActivityCategory;
  clubs: Array<{ id: string; name: string }>;
  existingScores: ClubActivityScore[];
  /** Submissions de esta actividad; se usan para presembrar timingBandIndex
   * cuando el modo es `timing` y aún no hay score guardado para el club. */
  submissions?: ClubActivitySubmission[];
}) {
  const { user } = useAuthStore();
  const options = useMemo(
    () => buildBatchOptions(activity.scoreMode, activity.scoreConfig),
    [activity.scoreMode, activity.scoreConfig],
  );

  // Estado: clubId → input (parcial). Solo los que el admin haya tocado o
  // que ya existieran tienen entrada definida.
  const [inputs, setInputs] = useState<Record<string, ActivityScoreInput>>({});
  // Set de clubIds cuyo puntaje existente se quiere ELIMINAR al guardar.
  const [pendingClears, setPendingClears] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const existingByClub = useMemo(() => {
    const m: Record<string, ClubActivityScore> = {};
    existingScores.forEach((s) => (m[s.clubId] = s));
    return m;
  }, [existingScores]);

  // #4: detectar puntajes guardados con un scoreMode anterior (shape distinto
  // al actual). El admin debe limpiarlos manualmente o quedarán huérfanos.
  const orphanScores = useMemo(() => {
    const expectedKey: Record<string, keyof ActivityScoreInput | null> = {
      podium: "position",
      pass_fail: "passed",
      fixed: "delivered",
      timing: "timingBandIndex",
      deductive: "deductionPoints",
      accumulative: "unitsCompleted",
    };
    const key = expectedKey[activity.scoreMode];
    if (!key) return [] as ClubActivityScore[];
    return existingScores.filter((s) => {
      const v = (s.input as Record<string, unknown>)[key];
      return v === undefined || v === null;
    });
  }, [existingScores, activity.scoreMode]);

  // Inicializar con los puntajes existentes cada vez que se abre.
  // También guardamos un "baseline" snapshot de IDs+scoredAt para detectar
  // cambios externos (otro admin guardando en paralelo). #5
  const [baseline, setBaseline] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (!isOpen) return;
    const seed: Record<string, ActivityScoreInput> = {};
    const snap = new Map<string, number>();
    existingScores.forEach((s) => {
      seed[s.clubId] = { ...s.input };
      snap.set(s.id, scoredAtMillis(s.scoredAt));
    });
    // Presembrar timingBandIndex desde submissions cuando el modo es `timing`
    // y aún no hay score guardado para ese club: evita re-marcar manualmente
    // "A tiempo" para cada club que ya entregó.
    if (activity.scoreMode === "timing" && submissions) {
      submissions.forEach((sub) => {
        if (sub.activityId !== activity.id) return;
        if (!sub.clubId) return;
        if (seed[sub.clubId]) return;
        if (sub.timingBandIndex === undefined || sub.timingBandIndex === null) {
          return;
        }
        seed[sub.clubId] = { timingBandIndex: sub.timingBandIndex };
      });
    }
    setInputs(seed);
    setPendingClears(new Set());
    setBaseline(snap);
    // Solo en apertura: si re-sembramos en cada cambio de existingScores
    // perderíamos los cambios sin guardar del admin actual.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ¿Se detectó una actualización externa desde que abrimos? #5
  const externalChange = useMemo(() => {
    if (!isOpen || (baseline.size === 0 && existingScores.length === 0))
      return null;
    const changed: ClubActivityScore[] = [];
    const added: ClubActivityScore[] = [];
    const seenIds = new Set<string>();
    for (const s of existingScores) {
      seenIds.add(s.id);
      const prev = baseline.get(s.id);
      if (prev === undefined) {
        added.push(s);
      } else if (scoredAtMillis(s.scoredAt) !== prev) {
        changed.push(s);
      }
    }
    const removed: string[] = [];
    baseline.forEach((_, id) => {
      if (!seenIds.has(id)) removed.push(id);
    });
    if (changed.length === 0 && added.length === 0 && removed.length === 0) {
      return null;
    }
    return { changed, added, removedCount: removed.length };
  }, [isOpen, existingScores, baseline]);

  const reloadFromServer = () => {
    const seed: Record<string, ActivityScoreInput> = {};
    const snap = new Map<string, number>();
    existingScores.forEach((s) => {
      seed[s.clubId] = { ...s.input };
      snap.set(s.id, scoredAtMillis(s.scoredAt));
    });
    setInputs(seed);
    setPendingClears(new Set());
    setBaseline(snap);
  };

  if (!options) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={activity.name} size="md">
        <div className="p-4 text-sm text-gray-700">
          Este modo de puntaje requiere capturar valores numéricos por club. Usa
          el modal individual para cada club.
        </div>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  const setOption = (clubId: string, opt: BatchOption | null) => {
    setInputs((prev) => {
      const next = { ...prev };
      if (!opt) {
        delete next[clubId];
        return next;
      }
      const base = prev[clubId] ?? {};
      let val = opt.apply(base);
      // Si la opción es exclusiva (ej: 1° lugar en podium), quitar a quien la
      // tuviera previamente.
      if (opt.exclusive) {
        for (const otherId of Object.keys(next)) {
          if (otherId === clubId) continue;
          if (opt.matches(next[otherId])) {
            // Limpiar la dimensión relevante (en podium, position).
            const cleaned = { ...next[otherId], position: undefined };
            // Si el input quedó vacío, lo eliminamos.
            if (
              cleaned.position === undefined &&
              cleaned.passed === undefined &&
              cleaned.delivered === undefined &&
              cleaned.timingBandIndex === undefined
            ) {
              delete next[otherId];
              // Si ese club tenía existing, marcarlo para borrar al guardar.
              if (existingByClub[otherId]) {
                setPendingClears((p) => {
                  const n = new Set(p);
                  n.add(otherId);
                  return n;
                });
              }
            } else {
              next[otherId] = cleaned;
            }
          }
        }
      }
      next[clubId] = val;
      return next;
    });
    // Si el club tenía pending clear y ahora le asignamos algo, cancelarlo.
    if (opt) {
      setPendingClears((p) => {
        if (!p.has(clubId)) return p;
        const n = new Set(p);
        n.delete(clubId);
        return n;
      });
    }
  };

  /**
   * Limpia la asignación de un club. Si existía en BD, marca para borrar al
   * guardar. Si solo era un cambio en memoria, simplemente lo descarta.
   */
  const clearClub = (clubId: string) => {
    setInputs((prev) => {
      const next = { ...prev };
      delete next[clubId];
      return next;
    });
    if (existingByClub[clubId]) {
      setPendingClears((p) => {
        const n = new Set(p);
        n.add(clubId);
        return n;
      });
    }
  };

  const fillColumn = (opt: BatchOption) => {
    if (opt.exclusive) {
      toast.error(`"${opt.label}" es exclusiva: solo un club puede tenerla.`);
      return;
    }
    setInputs((prev) => {
      const next = { ...prev };
      clubs.forEach((c) => {
        next[c.id] = opt.apply(prev[c.id] ?? {});
      });
      return next;
    });
  };

  // ¿Hay duplicados en columnas exclusivas?
  const exclusiveConflicts = useMemo(() => {
    const map: Record<string, string[]> = {};
    options.forEach((o) => {
      if (!o.exclusive) return;
      const ids = clubs
        .map((c) => c.id)
        .filter((id) => inputs[id] && o.matches(inputs[id]));
      if (ids.length > 1) map[o.key] = ids;
    });
    return map;
  }, [options, clubs, inputs]);

  // Cuántos cambios efectivos vamos a guardar (upserts + deletes).
  const pendingCount = useMemo(() => {
    let n = pendingClears.size;
    clubs.forEach((c) => {
      const cur = inputs[c.id];
      const prev = existingByClub[c.id]?.input;
      if (!cur) return;
      if (!prev || !sameInput(cur, prev ?? {})) n++;
    });
    return n;
  }, [clubs, inputs, existingByClub, pendingClears]);

  const handleSave = async () => {
    if (Object.keys(exclusiveConflicts).length > 0) {
      toast.error(
        "Resuelve los conflictos de posición exclusiva antes de guardar",
      );
      return;
    }
    setSaving(true);
    try {
      const scoredByName = user?.displayName || user?.email || "Usuario";
      const ops: Promise<unknown>[] = [];
      // Upserts
      for (const c of clubs) {
        const cur = inputs[c.id];
        const prev = existingByClub[c.id]?.input;
        if (!cur) continue;
        if (prev && sameInput(cur, prev)) continue;
        ops.push(
          upsertScore({
            eventId,
            activityId: activity.id,
            activityName: activity.name,
            categoryId: category.id,
            categoryName: category.name,
            clubId: c.id,
            clubName: c.name,
            scoreMode: activity.scoreMode,
            scoreConfig: activity.scoreConfig,
            input: cur,
            scoredByName,
          }),
        );
      }
      // Deletes
      const deletesCount = pendingClears.size;
      for (const clubId of pendingClears) {
        const ex = existingByClub[clubId];
        if (!ex) continue;
        ops.push(
          deleteScore(ex.id ?? buildScoreId(eventId, activity.id, clubId)),
        );
      }
      if (ops.length === 0) {
        toast("Sin cambios para guardar", { icon: "ℹ️" });
        onClose();
        return;
      }
      await Promise.all(ops);
      const upsertsCount = ops.length - deletesCount;
      const parts: string[] = [];
      if (upsertsCount > 0) parts.push(`${upsertsCount} guardado(s)`);
      if (deletesCount > 0) parts.push(`${deletesCount} borrado(s)`);
      toast.success(parts.join(" · "));
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Registrar puntajes · ${activity.name}`}
      description={`${category.name} · ${SCORE_MODE_LABEL[activity.scoreMode]}`}
      size="xl"
    >
      <div className="space-y-3">
        {clubs.length === 0 ? (
          <div className="p-4 text-sm text-gray-700 text-center">
            No hay clubes inscritos.
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-600">
              Marca por cada club la opción correspondiente. Los radios en gris
              indican el valor anterior. Solo se guardan los clubes con cambios.
            </div>
            {externalChange && (
              <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-xs text-blue-900 flex items-start gap-3">
                <div className="flex-1">
                  <p className="font-semibold mb-1">
                    🔄 Otro usuario actualizó puntajes mientras editabas
                  </p>
                  <p>
                    {externalChange.changed.length > 0 &&
                      `${externalChange.changed.length} modificado(s)`}
                    {externalChange.added.length > 0 &&
                      `${externalChange.changed.length > 0 ? " · " : ""}${externalChange.added.length} añadido(s)`}
                    {externalChange.removedCount > 0 &&
                      `${externalChange.changed.length + externalChange.added.length > 0 ? " · " : ""}${externalChange.removedCount} borrado(s)`}
                    . Si guardas ahora, sobreescribirás sus cambios en los
                    clubes que tengas modificados.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={reloadFromServer}
                  className="px-2 py-1 rounded border border-blue-400 bg-white text-blue-700 hover:bg-blue-100 whitespace-nowrap"
                >
                  Recargar
                </button>
              </div>
            )}
            {orphanScores.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="font-semibold mb-1">
                  ⚠️ {orphanScores.length} puntaje(s) con formato anterior
                </p>
                <p className="mb-2">
                  El modo de puntaje de esta actividad cambió (ahora{" "}
                  <b>{SCORE_MODE_LABEL[activity.scoreMode]}</b>) y los
                  siguientes clubes tienen valores guardados con el modo
                  anterior. No suman puntos hasta que los actualices o los
                  borres con el botón <i>limpiar</i>:
                </p>
                <ul className="list-disc pl-5 space-y-0.5">
                  {orphanScores.map((s) => (
                    <li key={s.id}>
                      <span className="font-medium">{s.clubName}</span>
                      <span className="text-amber-700">
                        {" "}
                        · {s.points} pt(s)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="overflow-x-auto overflow-y-auto max-h-[55vh] rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-700 sticky left-0 bg-gray-50 z-20">
                      Club
                    </th>
                    {options.map((o) => (
                      <th
                        key={o.key}
                        className="px-2 py-2 text-center font-medium text-gray-700 whitespace-nowrap"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span>
                            {o.label}
                            <span className="ml-1 text-xs text-gray-500">
                              ({o.points} pts)
                            </span>
                          </span>
                          {o.exclusive ? (
                            <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                              exclusiva
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => fillColumn(o)}
                              className="text-[10px] text-purple-700 hover:underline"
                            >
                              Marcar todos
                            </button>
                          )}
                          {exclusiveConflicts[o.key] && (
                            <span className="text-[10px] text-red-600">
                              Conflicto: {exclusiveConflicts[o.key].length}{" "}
                              clubes
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="px-2 py-2 text-center font-medium text-gray-500">
                      —
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {clubs.map((c, rowIdx) => {
                    const cur = inputs[c.id];
                    const prev = existingByClub[c.id]?.input;
                    const willDelete = pendingClears.has(c.id);
                    const isDirty =
                      willDelete ||
                      (cur && !prev) ||
                      (cur && prev && !sameInput(cur, prev)) ||
                      false;
                    return (
                      <tr
                        key={c.id}
                        className={
                          rowIdx % 2 === 0
                            ? "bg-white hover:bg-purple-50/40"
                            : "bg-gray-50/50 hover:bg-purple-50/40"
                        }
                      >
                        <td className="px-3 py-2 sticky left-0 bg-inherit z-10">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {c.name}
                            </span>
                            {isDirty && !willDelete && (
                              <span
                                className="w-2 h-2 rounded-full bg-purple-600"
                                title="Cambios sin guardar"
                              />
                            )}
                            {willDelete && (
                              <span
                                className="text-[10px] text-red-700 bg-red-100 px-1.5 py-0.5 rounded"
                                title="Se borrará al guardar"
                              >
                                se borrará
                              </span>
                            )}
                          </div>
                        </td>
                        {options.map((o) => {
                          const checked = cur ? o.matches(cur) : false;
                          const wasChecked = prev ? o.matches(prev) : false;
                          return (
                            <td key={o.key} className="px-2 py-2 text-center">
                              <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setOption(c.id, o)}
                                className={`inline-flex items-center justify-center w-7 h-7 rounded-full cursor-pointer transition ${
                                  checked
                                    ? "bg-purple-600 text-white"
                                    : wasChecked
                                      ? "bg-gray-200 text-gray-500 hover:bg-gray-300"
                                      : "bg-white border border-gray-300 hover:bg-purple-50"
                                }`}
                                title={o.label}
                              >
                                {checked ? "✓" : ""}
                              </button>
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => clearClub(c.id)}
                            disabled={!cur && !existingByClub[c.id]}
                            className="text-xs text-gray-400 hover:text-red-600 disabled:opacity-30"
                            title={
                              existingByClub[c.id]
                                ? "Borrar el puntaje guardado al confirmar"
                                : "Quitar selección"
                            }
                          >
                            limpiar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving || pendingCount === 0}>
          {saving
            ? "Guardando..."
            : pendingCount === 0
              ? "Sin cambios"
              : `Guardar ${pendingCount} cambio(s)`}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
